import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import { confirm, showAlert } from 'actions/app';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import API from 'services/API';
import { SearchProjectCollaborationsRoles } from 'services/Authorization';
import strings from 'strings';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import Text from 'UI/components/atoms/Text';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import ProfileAvatar from 'UI/components/molecules/ProfileAvatar';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { Roles } from 'UI/constants/roles';
import { SvgSubtractCircle } from 'UI/res';
import { replaceByMap } from 'UI/utils';

import {
  getStateAfterRemove,
  getStateAfterSelect,
  isValidCollaborator
} from './CollaborationsForm.utils';
import { useStyles } from './styles';
import { collabsMapper, renderAvatarTitle } from './utils';

const { dialogs: collabsDialog } = strings.searchProjects.collabsForm;
const { alerts: alertsCopies } = strings.searchProjects;

const getFinalEntityCopy = length =>
  length > 1 ? alertsCopies.entities.collaborators : alertsCopies.entities.collaborator;

const CollaborationsForm = ({ initCollabs = [], onClose, onSetCollaborators, searchProject }) => {
  const classes = useStyles();
  const [collaborators, setCollaborators] = useState({
    owner: initCollabs.find(collab => collab.isOwner),
    collabs: initCollabs,
    collabsToAdd: [],
    collabsToRemove: []
  });
  const [drawerUiState, setDrawerUiState] = useState({
    isSaving: false
  });
  const dispatch = useDispatch();
  const { id: searchProjectId, name: searchProjectName } = searchProject;

  const isOwner = !!collaborators.owner;
  const haveCollabsChanged = useMemo(
    () => collaborators.collabsToAdd.length !== 0 || collaborators.collabsToRemove.length !== 0,
    [collaborators.collabsToAdd, collaborators.collabsToRemove]
  );

  const currentCollaborations = useMemo(() => {
    const collabsIdsToRemove = collaborators.collabsToRemove.map(collabs => collabs.id);
    const currentCollabs = [...collaborators.collabs, ...collaborators.collabsToAdd].filter(
      collabs => !collabsIdsToRemove.includes(collabs.id)
    );
    const owners = currentCollabs.filter(collab => collab.isOwner);
    const onlyOneOwnerLeft = owners.length === 1;
    return onlyOneOwnerLeft
      ? currentCollabs.map(collab =>
          collab.isOwner ? { ...collab, disableRemoveButton: true } : collab
        )
      : currentCollabs;
  }, [collaborators.collabs, collaborators.collabsToAdd, collaborators.collabsToRemove]);

  const handleSelectCollaborator = (_, value) => {
    if (!isValidCollaborator(value)) return;

    const { email, full_name: fullName, id: userId, initials } = value;
    const collaborator = {
      detail: SearchProjectCollaborationsRoles.Collaborator,
      email,
      id: userId,
      initials,
      title: fullName
    };
    const isAlreadySelected = currentCollaborations.some(({ id }) => id === userId);

    if (isAlreadySelected) {
      dispatch(
        showAlert({
          severity: 'warning',
          title: alertsCopies.success.collaborations.userAlreadySelected.title,
          body: strings.formatString(alertsCopies.success.collaborations.userAlreadySelected.body, {
            name: fullName
          })
        })
      );
    }

    setCollaborators(prev => getStateAfterSelect(prev, collaborator));
  };

  const handleRemoveCollaborator = collaborator => () =>
    setCollaborators(prev => getStateAfterRemove(prev, collaborator));

  const handleDiscardCollabsChanges = () => {
    setCollaborators(prev => ({
      ...prev,
      collabsToAdd: [],
      collabsToRemove: []
    }));
    onClose();
  };

  const handleCloseForm = () => {
    haveCollabsChanged
      ? dispatch(
          confirm({
            severity: 'warning',
            ...collabsDialog.discardCollabsChanges,
            changeButtonsOrder: true,
            isHighLightActionOnLeft: true,
            onConfirm: ok => {
              if (!ok) return;
              setTimeout(() => {
                handleManageCollaborators();
              }, 50);
            },
            onClose: handleDiscardCollabsChanges
          })
        )
      : onClose();
  };

  const manageCollaborationsActions = {
    add: async collaboratorsAux => {
      try {
        const endpoint = Endpoints.SearchProjectsAddCollaborator.replace(/:id/, searchProjectId);
        const payload = {
          userIds: collaboratorsAux.collabsToAdd.map(({ id }) => id)
        };
        const response = await API.post(endpoint, payload);

        if (response.status === HTTPStatusCodes.Created) {
          const { data: collabsList } = response;
          const currentCollabsIds = collaboratorsAux.collabs.map(({ id }) => id);
          const newCollabs = collabsList.filter(
            ({ user_id }) => !currentCollabsIds.includes(user_id)
          );
          const newCollabsMapped = newCollabs.map(collabsMapper);
          const updatedCollabs = [...collaboratorsAux.collabs, ...newCollabsMapped];

          const finalEntityCopy = getFinalEntityCopy(newCollabsMapped.length);

          dispatch(
            showAlert({
              severity: 'success',
              autoHideDuration: 8000,
              title: strings.formatString(alertsCopies.success.generalTitle, {
                action: alertsCopies.success.actions.add,
                entity: finalEntityCopy
              }),
              body: strings.formatString(alertsCopies.success.collaborations.add, {
                entitiesLength: newCollabsMapped.length,
                entity: finalEntityCopy,
                searchProjectName
              })
            })
          );

          return {
            ...collaboratorsAux,
            collabs: updatedCollabs,
            collabsToAdd: []
          };
        }
        return null;
      } catch (error) {
        dispatch(
          showAlert({
            severity: 'error',
            title: strings.formatString(alertsCopies.error.general.title, {
              action: alertsCopies.error.actions.add,
              entity: getFinalEntityCopy(collaboratorsAux.collabsToAdd.length)
            }),
            body: alertsCopies.error.general.body
          })
        );
        return null;
      }
    },
    remove: async collaboratorsAux => {
      try {
        const currentCollabsIds = collaboratorsAux.collabs.map(({ id }) => id);
        const collabsThatCanBeRemoved = collaboratorsAux.collabsToRemove.filter(({ id }) =>
          currentCollabsIds.includes(id)
        );
        const mappedCollabsIdsToRemove = collabsThatCanBeRemoved.map(
          ({ collaborationId }) => collaborationId
        );

        if (mappedCollabsIdsToRemove.length === 0) return null;

        const replaceMap = {
          ':id': searchProjectId,
          ':collabIds': mappedCollabsIdsToRemove.join(',')
        };
        const endpoint = replaceByMap(
          Endpoints.SearchProjectsRemoveCollaborator,
          /:id|:collabIds/gi,
          replaceMap
        );

        // TODO: refactor collaborations form to use makeRequest util
        const response = await API.delete(endpoint);

        if (response.status === HTTPStatusCodes.Ok) {
          const removeIds = collaboratorsAux.collabsToRemove.map(({ id }) => id);
          const updatedCollabs = collaboratorsAux.collabs.filter(
            collab => !removeIds.includes(collab.id)
          );

          const finalEntityCopy = getFinalEntityCopy(removeIds.length);

          dispatch(
            showAlert({
              severity: 'success',
              title: strings.formatString(alertsCopies.success.generalTitle, {
                action: alertsCopies.success.actions.remove,
                entity: finalEntityCopy
              }),
              body: strings.formatString(alertsCopies.success.collaborations.remove, {
                entity: finalEntityCopy,
                entitiesLength: removeIds.length,
                searchProjectName
              })
            })
          );

          return {
            ...collaboratorsAux,
            collabs: updatedCollabs,
            collabsToRemove: []
          };
        }
        return null;
      } catch (error) {
        dispatch(
          showAlert({
            severity: 'error',
            title: strings.formatString(alertsCopies.error.general.title, {
              action: alertsCopies.error.actions.remove,
              entity: getFinalEntityCopy(collaboratorsAux.collabsToRemove.length)
            }),
            body: alertsCopies.error.general.body
          })
        );
        return null;
      }
    }
  };

  const handleManageCollaborators = async e => {
    e && e.preventDefault();

    if (!haveCollabsChanged || drawerUiState.isSaving) return;

    const { collabsToAdd, collabsToRemove } = collaborators;
    const { add, remove } = manageCollaborationsActions;
    const { dialog: saveChangesCopies, singular, plural } = collabsDialog.saveCollabsChanges;
    const setCurrentCollabs = updatedCollabs => {
      if (!updatedCollabs) {
        setDrawerUiState(prev => ({ ...prev, isSaving: false }));
        return;
      }

      onSetCollaborators && onSetCollaborators(updatedCollabs.collabs);
      setCollaborators(updatedCollabs);
      setDrawerUiState(prev => ({ ...prev, isSaving: false }));
      onClose();
    };

    if (collabsToRemove.length === 0) {
      setDrawerUiState(prev => ({ ...prev, isSaving: true }));
      const updatedCollabs = await add(collaborators);
      setCurrentCollabs(updatedCollabs);
    } else {
      dispatch(
        confirm({
          severity: 'warning',
          ...saveChangesCopies,
          message: strings.formatString(
            saveChangesCopies.message,
            collabsToRemove.length > 1 ? plural : singular
          ),
          onClose: () => {
            const currentUsersIds = collaborators.collabs.map(({ id }) => id);
            const collabsToKeep = collaborators.collabsToRemove.filter(
              ({ id }) => !currentUsersIds.includes(id)
            );
            setCollaborators(prev => ({
              ...prev,
              collabsToAdd: [...prev.collabsToAdd, ...collabsToKeep],
              collabsToRemove: []
            }));
          },
          onConfirm: async () => {
            setDrawerUiState(prev => ({ ...prev, isSaving: true }));
            const shouldAddCollaborators = collabsToAdd.length > 0;
            const shouldRemoveCollaborators = collabsToRemove.length > 0;

            if (shouldAddCollaborators && shouldRemoveCollaborators) {
              const updatedCollabsAfterAdding = await add(collaborators);

              if (!updatedCollabsAfterAdding) {
                setCurrentCollabs(null);
                return;
              }

              const updatedCollabsAfterRemoving = await remove(updatedCollabsAfterAdding);

              if (!updatedCollabsAfterRemoving) {
                onSetCollaborators && onSetCollaborators(updatedCollabsAfterAdding.collabs);
                setCollaborators(updatedCollabsAfterAdding);
                setDrawerUiState(prev => ({ ...prev, isSaving: false }));
                return;
              }

              setCurrentCollabs(updatedCollabsAfterRemoving);
            } else if (shouldAddCollaborators) {
              setCurrentCollabs(await add(collaborators));
            } else if (shouldRemoveCollaborators) {
              setCurrentCollabs(await remove(collaborators));
            }
          },
          body: (
            <div className={classes.saveChangesAvatars}>
              {collabsToRemove.map(({ id, detail, email, initials, title }) => (
                <ProfileAvatar
                  key={id}
                  showDetails
                  detail={detail}
                  email={email}
                  initials={initials}
                  title={renderAvatarTitle({ title, initials })}
                  avatarProps={{
                    size: 'medium'
                  }}
                />
              ))}
            </div>
          )
        })
      );
    }
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: handleCloseForm,
        disabled: drawerUiState.isSaving
      }
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        variant: 'contained',
        type: 'submit',
        disabled: !haveCollabsChanged || drawerUiState.isSaving,
        onProgressText: strings.shared.ui.saving,
        isSaving: drawerUiState.isSaving
      },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{ title: strings.searchProjects.collabsForm.title }}
      FPDrawerProps={{ open: true }}
      onClose={handleCloseForm}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: handleManageCollaborators }}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <Text
        aria-label="collabs-label"
        text="Select a Recruiter to add as Collaborator"
        variant="body1"
        fontSize={16}
      />
      <AutocompleteSelect
        name="selectedRecruiter"
        placeholder="Search a Recruiter by name or email *"
        typeahead
        typeaheadLimit={15}
        typeaheadParams={{ role_id: [Roles.Recruiter, Roles.Coach].join(',') }}
        url={Endpoints.Users}
        onSelect={handleSelectCollaborator}
        renderOption={({ full_name, initials }) =>
          renderAvatarTitle({ title: full_name, initials })
        }
      />
      <div className={classes.formCollabsContainer}>
        <List aria-label="collabs-list">
          {isOwner && (
            <ProfileAvatar
              {...collaborators.owner}
              avatarProps={{ size: 'medium' }}
              showDetails
              title={renderAvatarTitle(collaborators.owner)}
            />
          )}
          {currentCollaborations.length > 1 && <Divider />}
          {currentCollaborations.map(collaborator => {
            const { id, title, detail, initials, email, disableRemoveButton } = collaborator;

            return (
              !collaborator.isOwner && (
                <div key={id} aria-label={`avatar-li-${id}`} className={classes.formCollabs}>
                  <ProfileAvatar
                    avatarProps={{ size: 'medium' }}
                    title={renderAvatarTitle({ title, initials })}
                    detail={detail}
                    initials={initials}
                    email={email}
                    showDetails
                  />
                  <FPIconButton
                    icon={SvgSubtractCircle}
                    disabled={!!disableRemoveButton}
                    onClick={handleRemoveCollaborator(collaborator)}
                    tooltipProps={{ placement: 'bottom', title: 'Remove Collaborator' }}
                  />
                </div>
              )
            );
          })}
        </List>
      </div>
    </FPDrawer>
  );
};

export default CollaborationsForm;
